import { BRANDS } from "~constants";
import { ARTICLE_TYPE, Story, OPINION_ARTICLE_TYPE } from "~types/types";

import isOpinion from "./isOpinion";
import { capitalizeDashedString, capitalizeString } from "./stringUtil";

const NEWSLETTER_PATH = "account/newsletters";

function isWeekendEdition(story: Story) {
    const { brand } = story;

    return brand === BRANDS.WEEKEND_EDITION;
}

type EyebrowNewsletter = {
    default: {
        text?: string;
        path?: string;
    };
    brand: {
        [key: string]: {
            text?: string;
            path?: string;
        };
    };
};

type EyebrowBasic = {
    brand: {
        [key: string]: {
            text?: string;
            path?: string;
        };
    };
    franchise: {
        [key: string]: {
            text: string;
        };
    };
};

type EyebrowFeature = {
    brand: {
        [key: string]: {
            text?: string;
            path?: string;
        };
    };
    franchise: {
        [key: string]: {
            text: string;
        };
    };
};

type EyebrowDefault = {
    default: {
        text: string;
        path?: string;
    };
};

type EyebrowExplainers = {
    franchise: {
        [key: string]: {
            text: string;
        };
    };
};

type EyebrowOverrides = {
    [ARTICLE_TYPE.NEWSLETTER]: EyebrowNewsletter;
    [ARTICLE_TYPE.BASIC]: EyebrowBasic;
    [ARTICLE_TYPE.FEATURE]: EyebrowFeature;
    [ARTICLE_TYPE.LETTER]: EyebrowDefault;
    [ARTICLE_TYPE.QUICKTAKE]: EyebrowDefault;
    [ARTICLE_TYPE.EXPLAINERS]: EyebrowExplainers;
};

export const EYEBROW_OVERRIDES: EyebrowOverrides = {
    [ARTICLE_TYPE.NEWSLETTER]: {
        default: {
            text: "Newsletter",
            path: NEWSLETTER_PATH,
        },
        brand: {
            crypto: {
                text: "Newsletter",
                path: NEWSLETTER_PATH,
            },
            view: {
                text: "Opinion Newsletter",
                path: NEWSLETTER_PATH,
            },
        },
    },
    [ARTICLE_TYPE.BASIC]: {
        brand: {
            business: {
                path: "",
            },
            view: {
                text: "Opinion",
                path: "opinion",
            },
            gadfly: {
                text: "Opinion",
                path: "opinion",
            },
            citylab: {
                text: "CityLab",
            },
            "work-shift": {
                text: "Management & Work",
                path: "management-work",
            },
            "markets-magazine": {
                text: "Markets Magazine",
            },
        },
        franchise: {
            "deja-vu": {
                text: "Déjà Vu",
            },
            "the-ceo-diet": {
                text: "The CEO Diet",
            },
        },
    },
    [ARTICLE_TYPE.FEATURE]: {
        brand: {
            business: {
                path: "",
            },
            view: {
                text: "Opinion",
                path: "opinion",
            },
            gadfly: {
                text: "Opinion",
                path: "opinion",
            },
            citylab: {
                text: "CityLab",
            },
            "work-shift": {
                text: "Management & Work",
                path: "management-work",
            },
            "markets-magazine": {
                text: "Markets Magazine",
            },
        },
        franchise: {
            "deja-vu": {
                text: "Déjà Vu",
            },
            "the-ceo-diet": {
                text: "The CEO Diet",
            },
        },
    },
    [ARTICLE_TYPE.LETTER]: {
        default: {
            text: "Letter to the Editor",
        },
    },
    [ARTICLE_TYPE.QUICKTAKE]: {
        default: {
            text: "Quicktake",
        },
    },
    [ARTICLE_TYPE.EXPLAINERS]: {
        franchise: {
            "deja-vu": {
                text: "Déjà Vu",
            },
            "the-ceo-diet": {
                text: "The CEO Diet",
            },
        },
    },
};

export const getOpinionSuffix = (story: Story) => {
    const { authors, opinionArticleType } = story;

    if (!authors) return "";

    switch (opinionArticleType) {
        case OPINION_ARTICLE_TYPE.EDITORIAL_BOARD:
            return "Editorial Board";
        case OPINION_ARTICLE_TYPE.GUEST_ESSAY:
            return authors.length > 1 ? "Guest Columnists" : "Guest Columnist";
        case OPINION_ARTICLE_TYPE.COLUMNIST:
            return authors.length > 1 ? "Columnists" : "Columnist";
        case OPINION_ARTICLE_TYPE.MRB:
        default:
            return "";
    }
};

export const getEyebrowSubtext = (story: Story) => {
    if (isOpinion(story)) {
        const { authors, opinionArticleType } = story;

        if (
            !authors ||
            opinionArticleType === OPINION_ARTICLE_TYPE.EDITORIAL_BOARD
        ) {
            return null;
        }

        return authors.reduce((acc, { name }, idx, arr) => {
            if (idx === 0) {
                return name;
            }

            if (idx === arr.length - 1) {
                return `${acc} & ${name}`;
            }

            return `${acc}, ${name}`;
        }, "");
    }

    const { cobrand, label, franchise, pillar } = story;

    const type = getEyebrowType(story);

    if (
        type === ARTICLE_TYPE.BASIC ||
        type === ARTICLE_TYPE.FEATURE ||
        type === ARTICLE_TYPE.EXPLAINERS
    ) {
        const franchiseSubtext = franchise
            ? EYEBROW_OVERRIDES[type].franchise[franchise]?.text
            : "";

        return (
            capitalizeDashedString(cobrand) ||
            label ||
            franchiseSubtext ||
            capitalizeDashedString(franchise) ||
            capitalizeDashedString(pillar)
        );
    }

    return (
        capitalizeDashedString(cobrand) ||
        label ||
        capitalizeDashedString(pillar)
    );
};

export function getEyebrowLink(story: Story) {
    const { brand } = story;

    const type = getEyebrowType(story);

    if (
        type === ARTICLE_TYPE.SLIDESHOW ||
        type === ARTICLE_TYPE.BREAKING_NEWS
    ) {
        return {
            text: capitalizeString(brand),
            path: `/${brand}?source=eyebrow`,
        };
    }

    if (isWeekendEdition(story)) {
        return {
            text: getEyebrowSubtext(story) || "",
            path: "/weekend?source=eyebrow",
        };
    }

    const text = capitalizeString(brand);
    const path = `/${brand}?source=eyebrow`;

    const overrides = EYEBROW_OVERRIDES[type];
    const hasBrandOverrides = "brand" in overrides && overrides.brand[brand];
    const hasDefaultOverrides = "default" in overrides;

    if (hasBrandOverrides) {
        return {
            text: overrides.brand[brand].text || text,
            path: `/${overrides.brand[brand].path ?? brand}?source=eyebrow`,
        };
    }

    if (hasDefaultOverrides) {
        return {
            text: overrides.default.text || text,
            path: `/${overrides.default.path ?? brand}?source=eyebrow`,
        };
    }

    return { text, path };
}

export function getEyebrowSublink(story: Story) {
    if (isWeekendEdition(story)) {
        return { text: "", path: "" };
    }

    const { cobrand } = story;

    const text = getEyebrowSubtext(story) || "";
    const path = cobrand ? `/${cobrand}?source=eyebrow` : "";

    return { text, path };
}

function getEyebrowType(story: Story) {
    return story.type && story.type in EYEBROW_OVERRIDES
        ? story.type
        : ARTICLE_TYPE.BASIC;
}
